// cashflowService.js
// API service functions for General Transactions, Cash Flow, and Recurring Transactions

import API from './api';
import { format } from 'date-fns';

// ============================================================================
// General Transaction Tags
// ============================================================================

export const getGeneralTransactionTags = async (type = null) => {
  const params = {};
  if (type) params.type = type;
  const response = await API.get('/general-transaction-tags/', { params });
  return response.data;
};

export const createGeneralTransactionTag = async (data) => {
  const response = await API.post('/general-transaction-tags/create/', data);
  return response.data;
};

export const deleteGeneralTransactionTag = async (tagId) => {
  const response = await API.delete('/general-transaction-tags/delete/', {
    data: { tag_id: tagId }
  });
  return response.data;
};

// ============================================================================
// General Transactions
// ============================================================================

export const getGeneralTransactions = async (params = {}) => {
  const response = await API.get('/general-transactions/', { params });
  return response.data;
};

export const getGeneralTransaction = async (transactionId) => {
  const response = await API.get('/general-transaction/', {
    params: { transaction_id: transactionId }
  });
  return response.data;
};

export const createGeneralTransaction = async (data) => {
  // Format dates for API
  const payload = {
    type: data.type,
    tag_id: data.tag_id,
    amount: data.amount,
    actual_date: data.actual_date instanceof Date
      ? data.actual_date.toISOString()
      : data.actual_date,
    note: data.note || '',
    payment_methods: data.payment_methods || [],
  };

  // Format payment method dates
  if (payload.payment_methods && payload.payment_methods.length > 0) {
    payload.payment_methods = payload.payment_methods.map(pm => ({
      ...pm,
      check_due_date: pm.check_due_date
        ? format(pm.check_due_date, 'yyyy-MM-dd')
        : null,
      transfer_date: pm.transfer_date
        ? format(pm.transfer_date, 'yyyy-MM-dd')
        : null,
    }));
  }

  const response = await API.post('/general-transactions/create/', payload);
  return response.data;
};

export const updateGeneralTransaction = async (data) => {
  // data can include payment_method_id for granular updates
  const response = await API.put('/general-transactions/update/', data);
  return response.data;
};

export const deleteGeneralTransaction = async (transactionId, password, paymentMethodId = null) => {
  const payload = {
    transaction_id: transactionId,
    password: password,
  };

  // If payment_method_id is provided, only delete that payment method
  if (paymentMethodId) {
    payload.payment_method_id = paymentMethodId;
  }

  const response = await API.delete('/general-transactions/delete/', {
    data: payload
  });
  return response.data;
};

// ============================================================================
// Recurring Transactions
// ============================================================================

export const getRecurringTransactions = async (isActive = null) => {
  const params = {};
  if (isActive !== null) params.is_active = isActive;
  const response = await API.get('/recurring-transactions/', { params });
  return response.data;
};

export const createRecurringTransaction = async (data) => {
  const payload = {
    name: data.name,
    type: data.type,
    tag_id: data.tag_id,
    amount: data.amount,
    frequency: data.frequency,
    day_of_month: data.day_of_month,
    start_date: data.start_date instanceof Date
      ? format(data.start_date, 'yyyy-MM-dd')
      : data.start_date,
    end_date: data.end_date instanceof Date
      ? format(data.end_date, 'yyyy-MM-dd')
      : data.end_date,
    default_payment_method: data.default_payment_method || 'cash',
    note: data.note || '',
  };

  const response = await API.post('/recurring-transactions/create/', payload);
  return response.data;
};

export const updateRecurringTransaction = async (data) => {
  const payload = {
    recurring_id: data.recurring_id,
    name: data.name,
    // type is not included - cannot be changed after creation
    tag_id: data.tag_id,
    amount: data.amount,
    frequency: data.frequency,
    day_of_month: data.day_of_month,
    end_date: data.end_date instanceof Date
      ? format(data.end_date, 'yyyy-MM-dd')
      : data.end_date,
    default_payment_method: data.default_payment_method,
    note: data.note,
    is_active: data.is_active,
  };

  const response = await API.put('/recurring-transactions/update/', payload);
  return response.data;
};

export const deleteRecurringTransaction = async (recurringId) => {
  const response = await API.delete('/recurring-transactions/delete/', {
    data: { recurring_id: recurringId }
  });
  return response.data;
};

export const toggleRecurringTransaction = async (data) => {
  const response = await API.post('/recurring-transactions/toggle/', {
    recurring_id: data.recurring_id,
    is_active: data.is_active,
  });
  return response.data;
};

// ============================================================================
// Cash Flow
// ============================================================================

export const getCashFlow = async (params = {}) => {
  const response = await API.get('/cashflow/', { params });
  return response.data;
};

export const getChecks = async (params = {}) => {
  const response = await API.get('/cashflow/checks/', { params });
  return response.data;
};

export const getCashFlowSummary = async (period = 'month') => {
  const response = await API.get('/cashflow/summary/', { params: { period } });
  return response.data;
};

export const updateCheckStatus = async (referenceType, referenceId, status) => {
  const response = await API.post('/cashflow/checks/update-status/', {
    reference_type: referenceType,
    reference_id: referenceId,
    status: status,
  });
  return response.data;
};

// ============================================================================
// Cash Desk Management
// ============================================================================

export const getCashDesks = async (isActive = null) => {
  const params = {};
  if (isActive !== null) params.is_active = isActive;
  const response = await API.get('/cash-desks/', { params });
  return response.data;
};

export const createCashDesk = async (data) => {
  const response = await API.post('/cash-desk/create/', data);
  return response.data;
};

export const updateCashDesk = async (data) => {
  const response = await API.put('/cash-desk/update/', data);
  return response.data;
};

export const deleteCashDesk = async (cashDeskId) => {
  const response = await API.delete('/cash-desk/delete/', {
    data: { CashDeskID: cashDeskId }
  });
  return response.data;
};

// ============================================================================
// Cash Desk Submissions
// ============================================================================

export const getCashDeskSubmissions = async (params = {}) => {
  const response = await API.get('/cashdesk-submissions/', { params });
  return response.data;
};

export const createCashDeskSubmission = async (data) => {
  const payload = {
    CashDeskID: data.CashDeskID,
    PeriodType: data.PeriodType,
    PeriodDate: data.PeriodDate instanceof Date
      ? format(data.PeriodDate, 'yyyy-MM-dd')
      : data.PeriodDate,
    Amount: data.Amount,
    EmployeeExpenses: data.EmployeeExpenses || null,
    Note: data.Note || '',
    payment_methods: (data.payment_methods || []).map(pm => ({
      ...pm,
      check_due_date: pm.check_due_date
        ? format(pm.check_due_date, 'yyyy-MM-dd')
        : null,
      transfer_date: pm.transfer_date
        ? format(pm.transfer_date, 'yyyy-MM-dd')
        : null,
    })),
  };
  const response = await API.post('/cashdesk-submission/create/', payload);
  return response.data;
};

export const updateCashDeskSubmission = async (data) => {
  const response = await API.put('/cashdesk-submission/update/', data);
  return response.data;
};

export const deleteCashDeskSubmission = async (submissionId) => {
  const response = await API.delete('/cashdesk-submission/delete/', {
    data: { SubmissionID: submissionId }
  });
  return response.data;
};

// ============================================================================
// Cash Desk Metrics
// ============================================================================

export const getCashDeskMetrics = async (params = {}) => {
  const response = await API.get('/cashdesk-metrics/', { params });
  return response.data;
};
